/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Manager, Popper, Reference } from 'react-popper';

import type { Data as PopperModifierData } from 'popper.js';

import type { Theme } from '@coursera/cds-core';
import { useTheme } from '@coursera/cds-core';

const useOverlayTriggerStyles = (theme: Theme) => ({
  Arrow: css({
    position: 'absolute',
    display: 'block',
    width: '0',
    height: '0',
    ':before': {
      position: 'absolute',
      content: '""',
      borderColor: 'transparent',
      borderStyle: 'solid',
    },
  }),
  ArrowBottom: css({
    top: 0,
    transform: 'translate(-6px, -6px)',
    ':before': {
      borderWidth: '0 6px 6px 6px',
      borderBottomColor: theme.palette.black[700],
    },
  }),
  ArrowTop: css({
    transform: 'translate(-6px, 0)',
    ':before': {
      borderWidth: '6px 6px 0 6px',
      borderTopColor: theme.palette.black[700],
    },
  }),
  ArrowRight: css({
    transform: 'translate(-6px, -6px)',
    ':before': {
      borderWidth: '6px 6px 6px 0',
      borderRightColor: theme.palette.black[700],
    },
  }),
  ArrowLeft: css({
    right: 0,
    transform: 'translate(0rem, -6px)',
    ':before': {
      borderWidth: '6px 0 6px 6px',
      borderLeftColor: theme.palette.black[700],
    },
  }),
});

type OverlayTriggerProps = {
  placement: 'bottom' | 'top' | 'right' | 'left';
  overlay: React.ReactNode;
  delay?: number;
  show?: boolean;
  tag?: React.ElementType;
} & React.HTMLAttributes<HTMLElement>;

const TOOLTIP_GAP = 8;

const gapModifier = {
  enabled: true,

  // `computeStyle` modifier, which determines the `translate3d` style of the tooltip, has an order of 850,
  // and the previous modifier has an order of 800.
  // so we need an order between (800, 850) to make sure the position is modified before the styles are computed.
  order: 810,
  fn: (data: PopperModifierData): PopperModifierData => {
    const { popper } = data.offsets;
    let { left, top } = popper;

    if (data.placement === 'right') {
      left += TOOLTIP_GAP;
    } else if (data.placement === 'bottom') {
      top += TOOLTIP_GAP;
    } else if (data.placement === 'left') {
      left -= TOOLTIP_GAP;
    } else if (data.placement === 'top') {
      top -= TOOLTIP_GAP;
    }

    return {
      ...data,
      offsets: {
        ...data.offsets,
        popper: { ...popper, top, left },
      },
    };
  },
};

export const OverlayTrigger: React.FC<OverlayTriggerProps> = ({
  placement,
  children,
  overlay,
  show: controlledShow,
  tag: Wrapper = 'button',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  css: unused,
  delay = 0,
  ...otherProps
}) => {
  const [shouldDisplayPopper, setShouldDisplayPopper] = React.useState(false);
  const theme = useTheme();
  const styles = useOverlayTriggerStyles(theme);
  const showPopper = React.useCallback(() => setTimeout(() => setShouldDisplayPopper(true), delay), [delay]);

  const hidePopper = React.useCallback(() => setTimeout(() => setShouldDisplayPopper(false), delay), [delay]);

  return (
    <Manager>
      <Reference>
        {({ ref }) => {
          const props = {
            ...otherProps,
            ref,
            // Add `type` to prevent submitting form when used in forms

            type: Wrapper === 'button' ? ('button' as const) : undefined,
            onMouseOver: showPopper,
            onMouseLeave: hidePopper,
            onFocus: showPopper,
            onBlur: hidePopper,
          };

          return (
            <Wrapper
              css={{
                margin: 0,
                padding: 0,
                display: 'inline',
                backgroundColor: 'transparent',
                border: 0,
              }}
              {...props}
            >
              {children}
            </Wrapper>
          );
        }}
      </Reference>
      {controlledShow ?? shouldDisplayPopper ? (
        <Popper placement={placement} modifiers={{ gapModifier }}>
          {({ ref, style, placement: actualPlacement, arrowProps }) => (
            <div
              ref={ref}
              style={{
                ...style,
                zIndex: 1070,
              }}
              data-placement={placement}
            >
              {overlay}
              <div
                ref={arrowProps.ref}
                style={arrowProps.style}
                css={[
                  styles.Arrow,
                  {
                    bottom: styles.ArrowBottom,
                    top: styles.ArrowTop,
                    right: styles.ArrowRight,
                    left: styles.ArrowLeft,
                  }[actualPlacement as 'bottom' | 'top' | 'right' | 'left'],
                ]}
              />
            </div>
          )}
        </Popper>
      ) : null}
    </Manager>
  );
};

const tooltipStyle = (theme: Theme) =>
  css({
    maxWidth: '200px',
    whiteSpace: 'pre-wrap',
    color: 'var(--cds-color-white-0)',
    padding: '6px 12px',
    textAlign: 'center',
    backgroundColor: theme.palette.black[700],
    borderRadius: '3px',
    fontSize: '12px',
    textTransform: 'none',
    lineHeight: '16px',
    zIndex: 1070,
    fontWeight: theme.typography.fontWeight,
    fontFamily: theme.typography.fontFamily,
  });

export const Tooltip: React.FC<{}> = ({ children }) => {
  return <div css={tooltipStyle}>{children}</div>;
};
